import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
    ClipboardList,
    Network,
    LineChart,
    Users,
    MessagesSquare,
    Workflow,
    Check,
} from "lucide-react";
import { TID } from "../../lib/testIds";

const tabs = [
    {
        id: "assessments",
        label: "Assessments",
        icon: ClipboardList,
        title: "Assessment Engine",
        bullets: [
            "Question banks with adaptive sequencing",
            "Live proctoring + integrity signals",
            "Auto-grading for objective & subjective",
            "Cohort-level performance insights",
        ],
        mock: "exam",
    },
    {
        id: "lms",
        label: "Learning Management",
        icon: Network,
        title: "Learning Management",
        bullets: [
            "Modular curriculum & syllabus builder",
            "Live, recorded and hybrid classes",
            "Resource library with version history",
            "Outcome-based learning tracks",
        ],
        mock: "lms",
    },
    {
        id: "analytics",
        label: "Analytics",
        icon: LineChart,
        title: "Institutional Analytics",
        bullets: [
            "Real-time dashboards for leadership",
            "Predictive risk & drop-off models",
            "Drilldowns across batches & subjects",
            "Board-ready exports in one click",
        ],
        mock: "analytics",
    },
    {
        id: "lifecycle",
        label: "Student Lifecycle",
        icon: Users,
        title: "Student Lifecycle",
        bullets: [
            "Lead \u2192 enrollment \u2192 alumni in one CRM",
            "Smart segmentation & nudges",
            "Parent portals and timelines",
            "Fee plans, scholarships & finance",
        ],
        mock: "lifecycle",
    },
    {
        id: "comms",
        label: "Communication",
        icon: MessagesSquare,
        title: "Unified Communication",
        bullets: [
            "Email, SMS, WhatsApp & in-app",
            "Templates, approvals, audit trail",
            "Auto-triggers from any data event",
            "Granular role-based permissions",
        ],
        mock: "comms",
    },
    {
        id: "automation",
        label: "Automation",
        icon: Workflow,
        title: "Workflow Automation",
        bullets: [
            "No-code automation studio",
            "Scheduled & event-based triggers",
            "AI assist for repeat workflows",
            "Open APIs and webhooks",
        ],
        mock: "automation",
    },
];

const MockExam = () => (
    <div className="space-y-3">
        {[
            "Q1. Define orthogonal vectors.",
            "Q2. Solve \u222b x\u00b2 dx",
            "Q3. State Newton\u2019s second law.",
        ].map((q, i) => (
            <div
                key={i}
                className="flex items-center justify-between rounded-lg border border-black/[0.07] bg-[#FAFAFC] px-4 py-3 dark:border-white/10 dark:bg-white/[0.03]"
            >
                <span className="text-sm text-[#374151] dark:text-white/80">{q}</span>
                <span className="font-mono text-[10px] text-cyan-600 dark:text-brand-blue/80">
                    AUTO \u2713
                </span>
            </div>
        ))}
    </div>
);
const MockBars = ({ values }) => (
    <div className="flex items-end gap-2 h-32">
        {values.map((v, i) => (
            <motion.div
                key={i}
                initial={{ height: 0 }}
                animate={{ height: `${v}%` }}
                transition={{ duration: 0.8, delay: i * 0.05 }}
                className="flex-1 rounded-t bg-gradient-to-t from-cyan-400/60 to-violet-500/90 dark:from-brand-blue/40 dark:to-brand-purple/80"
            />
        ))}
    </div>
);
const MockRows = ({ rows }) => (
    <div className="space-y-2">
        {rows.map((r, i) => (
            <div
                key={i}
                className="flex items-center justify-between rounded-lg border border-black/[0.07] bg-[#FAFAFC] px-4 py-3 dark:border-white/10 dark:bg-white/[0.03]"
            >
                <span className="text-sm text-[#374151] dark:text-white/80">{r[0]}</span>
                <span className="font-mono text-[10px] text-[#9CA3AF] dark:text-white/50">
                    {r[1]}
                </span>
            </div>
        ))}
    </div>
);

const renderMock = (kind) => {
    switch (kind) {
        case "exam":
            return <MockExam />;
        case "lms":
            return (
                <MockRows
                    rows={[
                        ["Module 1 \u00b7 Foundations", "92% complete"],
                        ["Module 2 \u00b7 Advanced", "61% complete"],
                        ["Live Class \u00b7 Tonight 8pm", "Scheduled"],
                    ]}
                />
            );
        case "analytics":
            return <MockBars values={[40, 55, 30, 70, 65, 80, 50, 90]} />;
        case "lifecycle":
            return (
                <MockRows
                    rows={[
                        ["Aarav S. \u00b7 Lead", "Nurture"],
                        ["Priya M. \u00b7 Enrolled", "Active"],
                        ["Ravi K. \u00b7 At risk", "Outreach"],
                    ]}
                />
            );
        case "comms":
            return (
                <MockRows
                    rows={[
                        ["Parent broadcast \u00b7 Sent", "12,402"],
                        ["WhatsApp nudge \u00b7 Open", "94%"],
                        ["SMS reminder \u00b7 Queue", "Today 6pm"],
                    ]}
                />
            );
        case "automation":
            return (
                <MockRows
                    rows={[
                        ["When fee due \u2192 notify", "Active"],
                        ["When score < 40% \u2192 mentor", "Active"],
                        ["When idle 7d \u2192 re-engage", "Draft"],
                    ]}
                />
            );
        default:
            return null;
    }
};

const Solution = () => {
    const [active, setActive] = useState("assessments");
    const current = tabs.find((t) => t.id === active);
    const Icon = current.icon;

    return (
        <section
            id="solution"
            data-testid={TID.solutionSection}
            className="relative py-28 md:py-36 px-6"
        >
            <div className="max-w-7xl mx-auto relative z-10">
                <motion.div
                    initial={{ opacity: 0, y: 24 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-80px" }}
                    transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
                    className="max-w-3xl"
                >
                    <p className="font-mono text-xs tracking-[0.4em] uppercase text-violet-500 dark:text-brand-blue/80">
                        04 — The Solution
                    </p>
                    <h2 className="mt-5 font-display text-4xl sm:text-5xl md:text-6xl font-bold tracking-tight leading-[1.1] text-gradient-brand">
                        One platform.
                        <br />
                        <span className="text-gradient-accent">
                            Infinite possibilities.
                        </span>
                    </h2>
                    <p className="mt-6 text-[#6B7280] dark:text-white/55 leading-relaxed">
                        A single, deeply integrated ecosystem that replaces the
                        chaos of disconnected tools.
                    </p>
                </motion.div>

                <div className="mt-14 grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-10">
                    {/* Tabs */}
                    <div className="lg:col-span-4 flex flex-col gap-2">
                        {tabs.map((t) => {
                            const T = t.icon;
                            const isActive = t.id === active;
                            return (
                                <button
                                    key={t.id}
                                    onClick={() => setActive(t.id)}
                                    data-testid={`${TID.solutionTab}-${t.id}`}
                                    className={`relative flex items-center gap-3 px-5 py-4 rounded-xl text-left transition-all overflow-hidden ${
                                        isActive
                                            ? "bg-violet-50 border border-violet-200 dark:bg-white/[0.05] dark:border-white/15"
                                            : "border border-transparent hover:bg-[#FAFAFC] dark:hover:bg-white/[0.02]"
                                    }`}
                                >
                                    {isActive && (
                                        <motion.span
                                            layoutId="active-tab-glow"
                                            aria-hidden
                                            className="absolute inset-0 rounded-xl"
                                            style={{
                                                background:
                                                    "linear-gradient(120deg, rgba(139,92,246,0.08), rgba(34,211,238,0.05))",
                                            }}
                                        />
                                    )}
                                    <span
                                        className={`relative flex items-center justify-center w-9 h-9 rounded-lg border ${
                                            isActive
                                                ? "border-violet-300 bg-violet-100 dark:border-brand-purple/40 dark:bg-brand-purple/10"
                                                : "border-black/[0.08] bg-[#F9FAFB] dark:border-white/10 dark:bg-white/[0.02]"
                                        }`}
                                    >
                                        <T
                                            size={16}
                                            className={isActive ? "text-violet-600 dark:text-white" : "text-[#6B7280] dark:text-white/60"}
                                        />
                                    </span>
                                    <span className={`relative font-display text-base font-medium ${
                                        isActive
                                            ? "text-[#111827] dark:text-white"
                                            : "text-[#6B7280] dark:text-white/70"
                                    }`}>
                                        {t.label}
                                    </span>
                                </button>
                            );
                        })}
                    </div>

                    {/* Canvas */}
                    <div className="lg:col-span-8">
                        <div className="relative rounded-2xl border border-black/[0.07] bg-white shadow-[0_4px_24px_rgba(0,0,0,0.06)] p-8 overflow-hidden min-h-[440px] dark:border-white/[0.08] dark:bg-white/[0.02] dark:shadow-none dark:backdrop-blur-xl">
                            <div
                                aria-hidden
                                className="absolute inset-0 opacity-50 dark:opacity-70"
                                style={{
                                    background:
                                        "radial-gradient(600px circle at 80% -10%, rgba(139,92,246,0.08), transparent 60%)",
                                }}
                            />
                            <AnimatePresence mode="wait">
                                <motion.div
                                    key={current.id}
                                    initial={{ opacity: 0, y: 18 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    exit={{ opacity: 0, y: -10 }}
                                    transition={{
                                        duration: 0.5,
                                        ease: [0.16, 1, 0.3, 1],
                                    }}
                                    className="relative grid grid-cols-1 md:grid-cols-2 gap-8 items-start"
                                >
                                    <div>
                                        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#F3F4F6] border border-black/[0.06] font-mono text-[10px] tracking-[0.25em] uppercase text-[#6B7280] dark:bg-white/5 dark:border-white/10 dark:text-white/70">
                                            <Icon size={12} />
                                            {current.label}
                                        </div>
                                        <h3 className="mt-5 font-display text-2xl sm:text-3xl font-semibold text-[#111827] dark:text-white">
                                            {current.title}
                                        </h3>
                                        <ul className="mt-5 space-y-3">
                                            {current.bullets.map((b) => (
                                                <li
                                                    key={b}
                                                    className="flex items-start gap-3 text-sm text-[#6B7280] dark:text-white/70"
                                                >
                                                    <Check
                                                        size={14}
                                                        className="mt-1 text-cyan-500 dark:text-brand-blue"
                                                    />
                                                    {b}
                                                </li>
                                            ))}
                                        </ul>
                                    </div>
                                    <div className="relative">
                                        <div className="absolute -inset-4 rounded-2xl bg-gradient-to-br from-violet-100/60 to-cyan-50/60 blur-xl dark:from-brand-purple/10 dark:to-brand-blue/10" />
                                        <div className="relative rounded-xl border border-black/[0.07] bg-white/90 p-5 dark:border-white/10 dark:bg-[#06070F]/80">
                                            <div className="flex items-center justify-between mb-4">
                                                <span className="font-mono text-[10px] tracking-[0.25em] uppercase text-[#9CA3AF] dark:text-white/40">
                                                    Live Preview
                                                </span>
                                                <span className="flex gap-1">
                                                    <span className="w-1.5 h-1.5 rounded-full bg-black/10 dark:bg-white/20" />
                                                    <span className="w-1.5 h-1.5 rounded-full bg-black/10 dark:bg-white/20" />
                                                    <span className="w-1.5 h-1.5 rounded-full bg-violet-500 dark:bg-brand-purple" />
                                                </span>
                                            </div>
                                            {renderMock(current.mock)}
                                        </div>
                                    </div>
                                </motion.div>
                            </AnimatePresence>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
};

export default Solution;
